import { createApi } from "@reduxjs/toolkit/query/react";

import {
  baseQuery,
  GET_DASHBOARD_UPDATE_DATE,
  GET_POTENTIAL_COUNTRIES,
  GET_POTENTIAL_OPPORTUNITIES,
  GET_POTENTIAL_ORIGIN_VS_EXPORT,
  GET_POTENTIAL_ORIGIN_VS_MANUFACTURER,
  GET_POTENTIAL_PROGRAMS,
  GET_POTENTIAL_RELATED_VENDORS,
  GET_POTENTIAL_TARIFF_CODES,
  GET_POTENTIAL_VENDORS,
  GET_VISIBILITY_DUTY_SAVING_UTILIZED,
  GET_VISIBILITY_DUTY_SPEND,
  GET_VISIBILITY_IMPORT_EXPORT,
  GET_VISIBILITY_MANUFACTURERS,
  GET_VISIBILITY_METRICS,
  GET_VISIBILITY_OVERALL_IMPORTS,
  GET_VISIBILITY_TARIFF_HTS,
  GET_POTENTIAL_FTZ_SAVINGS,
  GET_POTENTIAL_MTB_SAVINGS,
  GET_POTENTIAL_EXCLUSION_SAVINGS,
  MUTATE_SEND_FIRST_SALE_EMAIL,
  GET_ALL_VENDORS,
} from "@/graphql";

export interface MTBSavingsResponse {
  data: Array<{
    htsCode: string;
    mtbNumber: string;
    oldDutyRate: string;
    newDutyRate: string;
    savings: number;
    goodsValue: number;
    description: string;
    status: string;
    expirationDate: string;
    entryCount: number;
  }>;
  total: {
    totalSavings: number;
    totalGoodsValue: number;
  };
  meta: {
    countDocs: number;
    pageNo: number;
    pageSize: number;
  };
}

export interface DashboardPotentialInput {
  dateFrom?: string;
  dateTo?: string;
  pageNo?: number;
  pageSize?: number;
  clientId?: string;
  attribute?: string;
}

export interface SendFirstSaleEmailInput {
  manufacturerId: string;
  to: string;
  clientId: string;
  manufacturerSubmissionId?: string;
}

export interface SendFirstSaleEmailResponse {
  data: {
    msg: string;
    status: string;
  };
}

export interface ExclusionSavingsResponse {
  data: {
    htsCode: string;
    code99: string;
    oldDutyRate: string;
    newDutyRate: string;
    savings: number;
    goodsValue: number;
    description: string;
    entryCount: number;
    active: boolean;
  }[];
  total: {
    totalSavings: number;
    totalGoodsValue: number;
  };
  meta: {
    countDocs: number;
    pageNo: number;
    pageSize: number;
  };
}

export interface DashboardUpdateDateResponse {
  lastUpdated: Date;
}

export const dashboardApi = createApi({
  reducerPath: "api/dashboard",
  tagTypes: ["Dashboards"],
  baseQuery,
  endpoints: (builder) => ({
    getDashboardOpportunities: builder.query<
      OpportunitiesReponse["dashboardPotentialOpportunities"],
      IDashboardPotentialVariables
    >({
      query: (variables) => ({
        document: GET_POTENTIAL_OPPORTUNITIES,
        variables: { ...variables },
      }),
      transformResponse: (response: OpportunitiesReponse) => {
        return response.dashboardPotentialOpportunities;
      },
    }),
    getPotentialVendors: builder.query<
      VendorsReponse["dashboardPotentialVendors"],
      IDashboardPotentialVariablesWithoutPagination
    >({
      query: (variables) => ({
        document: GET_POTENTIAL_VENDORS,
        variables: { ...variables },
      }),
      transformResponse: (response: VendorsReponse) => {
        return response.dashboardPotentialVendors;
      },
    }),
    getPotentialRelatedVendors: builder.query<
      RelatedVendorsReponse["dashboardPotentialRelatedUnrelatedVendors"],
      IDashboardPotentialVariables
    >({
      query: (variables) => ({
        document: GET_POTENTIAL_RELATED_VENDORS,
        variables: { ...variables },
      }),
      transformResponse: (response: RelatedVendorsReponse) => {
        return response.dashboardPotentialRelatedUnrelatedVendors;
      },
    }),
    getPotentialOriginManufactuers: builder.query<
      OriginXManufacturerReponse["dashboardPotentialOriginVsManufacturerId"],
      IDashboardPotentialVariables
    >({
      query: (variables) => ({
        document: GET_POTENTIAL_ORIGIN_VS_MANUFACTURER,
        variables: { ...variables },
      }),
      transformResponse: (response: OriginXManufacturerReponse) => {
        return response.dashboardPotentialOriginVsManufacturerId;
      },
    }),
    getAllVendors: builder.query<
      GetAllVendorsReponse["getFirstSaleData"],
      IGetAllVendorsVariables
    >({
      query: (variables) => ({
        document: GET_ALL_VENDORS,
        variables: { ...variables },
      }),
      transformResponse: (response: GetAllVendorsReponse) => {
        return response.getFirstSaleData;
      },
    }),
    getPotentialOriginExport: builder.query<
      OriginXExportReponse["dashboardPotentialOriginVsExport"],
      IDashboardPotentialVariables
    >({
      query: (variables) => ({
        document: GET_POTENTIAL_ORIGIN_VS_EXPORT,
        variables: { ...variables },
      }),
      transformResponse: (response: OriginXExportReponse) => {
        return response.dashboardPotentialOriginVsExport;
      },
    }),
    getDashboardUpdateDate: builder.query<
      string,
      { params: { clientId?: string } }
    >({
      query: (variables) => ({
        document: GET_DASHBOARD_UPDATE_DATE,
        variables,
      }),
      transformResponse: (response: { dataLastUpdate: any }) =>{
        return response.dataLastUpdate.data.lastUpdatedAt
      }
    }),
    getDashboardDutySavingUtilized: builder.query<
      DutySavingsUtilizedReponse["dashboardVisibilityDutySavingsUtilized"],
      IDashboardVisibilityVariables
    >({
      query: (variables) => ({
        document: GET_VISIBILITY_DUTY_SAVING_UTILIZED,
        variables: { ...variables },
      }),
      transformResponse: (response: DutySavingsUtilizedReponse) => {
        return response.dashboardVisibilityDutySavingsUtilized;
      },
    }),
    getDashboardManufacturers: builder.query<
      ManufacturersReponse["dashboardVisibilityGetManufacturers"],
      IDashboardVisibilityVariables
    >({
      query: (variables) => ({
        document: GET_VISIBILITY_MANUFACTURERS,
        variables: { ...variables },
      }),
      transformResponse: (response: ManufacturersReponse) => {
        return response.dashboardVisibilityGetManufacturers;
      },
    }),
    getDashboardImports: builder.query<
      ImportsValueReponse["dashboardVisibilityOverallImportValue"],
      IImportSVariables
    >({
      query: (variables) => ({
        document: GET_VISIBILITY_OVERALL_IMPORTS,
        variables: { ...variables },
      }),
      transformResponse: (response: ImportsValueReponse) => {
        return response.dashboardVisibilityOverallImportValue;
      },
    }),
    getDashboardMetrics: builder.query<
      IMetricsResponse["dashboardVisibilityImportMetrics"],
      IDashboardVisibilityVariables
    >({
      query: (variables) => ({
        document: GET_VISIBILITY_METRICS,
        variables: { ...variables },
      }),
      transformResponse: (response: IMetricsResponse) => {
        return response.dashboardVisibilityImportMetrics;
      },
    }),
    getDashboardTariffHts: builder.query<
      //.....
      ITariffHtsResponse["dashboardVisibilityLineTariffValueByHts"],
      ITariffHtsVariables
    >({
      query: (variables) => ({
        document: GET_VISIBILITY_TARIFF_HTS,
        variables: { ...variables },
      }),
      transformResponse: (response: ITariffHtsResponse) => {
        return response.dashboardVisibilityLineTariffValueByHts;
      },
    }),
    getDashboardDutySpend: builder.query<
      IDutySpendResponse["dashboardVisibilityDutySpend"],
      IDutySpendVariables
    >({
      query: (variables) => ({
        document: GET_VISIBILITY_DUTY_SPEND,
        variables: { ...variables },
      }),
      transformResponse: (response: IDutySpendResponse) => {
        return response.dashboardVisibilityDutySpend;
      },
    }),
    getDashboardImportsExports: builder.query<
      IImportExportResponse["dashboardVisibilityImportToExportTable"],
      IDashboardVisibilityVariables
    >({
      query: (variables) => ({
        document: GET_VISIBILITY_IMPORT_EXPORT,
        variables: { ...variables },
      }),
      transformResponse: (response: IImportExportResponse) => {
        return response.dashboardVisibilityImportToExportTable;
      },
    }),
    getPotentialDashboardTariffCodes: builder.query<
      ITariffCodesResponse["dashboardPotentialTariffCodes"],
      IDashboardPotentialVariables
    >({
      query: (variables) => ({
        document: GET_POTENTIAL_TARIFF_CODES,
        variables: { ...variables },
      }),
      transformResponse: (response: ITariffCodesResponse) => {
        return response.dashboardPotentialTariffCodes;
      },
    }),
    getPotentialDashboardPrograms: builder.query<
      IProgramsResponse["dashboardPotentialPrograms"],
      IDashboardPotentialVariables
    >({
      query: (variables) => ({
        document: GET_POTENTIAL_PROGRAMS,
        variables: { ...variables },
      }),
      transformResponse: (response: IProgramsResponse) => {
        return response.dashboardPotentialPrograms;
      },
    }),
    getPotentialDashboardCountries: builder.query<
      IPotentialCountriesResponse["dashboardPotentialCountries"],
      IDashboardPotentialVariables
    >({
      query: (variables) => ({
        document: GET_POTENTIAL_COUNTRIES,
        variables: { ...variables },
      }),
      transformResponse: (response: IPotentialCountriesResponse) => {
        return response.dashboardPotentialCountries;
      },
    }),
    getPotentialFTZSavings: builder.query<
      IFTZSavingsResponse["dashboardPotentialFTZSavings"],
      IDashboardPotentialVariables
    >({
      query: (variables) => ({
        document: GET_POTENTIAL_FTZ_SAVINGS,
        variables: { ...variables },
      }),
      transformResponse: (response: IFTZSavingsResponse) => {
        return response.dashboardPotentialFTZSavings;
      },
    }),
    getPotentialDashboardMTBSavings: builder.query<
      MTBSavingsResponse,
      { dashboardPotential: DashboardPotentialInput }
    >({
      query: (variables) => ({
        document: GET_POTENTIAL_MTB_SAVINGS,
        variables,
      }),
      transformResponse: (response: {
        dashboardPotentialMTBSavings: MTBSavingsResponse;
      }) => response.dashboardPotentialMTBSavings,
    }),
    getPotentialDashboardExclusionSavings: builder.query<
      ExclusionSavingsResponse,
      { dashboardPotential: DashboardPotentialInput }
    >({
      query: (variables) => ({
        document: GET_POTENTIAL_EXCLUSION_SAVINGS,
        variables,
      }),
      transformResponse: (response: {
        dashboardPotentialExclusionSavings: ExclusionSavingsResponse;
      }) => response.dashboardPotentialExclusionSavings,
    }),

    mutateSendFirstSaleEmail: builder.mutation<
      SendFirstSaleEmailResponse,
      SendFirstSaleEmailInput
    >({
      query: (variables) => ({
        document: MUTATE_SEND_FIRST_SALE_EMAIL,
        variables,
        headers: {
          Authorization: `${localStorage.getItem('accessToken')}`
        }
      }),
      transformResponse: (response: {
        sendFirstSaleEmail: SendFirstSaleEmailResponse;
      }) => response.sendFirstSaleEmail,
    }),
  }),
});

export const {
  // Potentials Dashboard ---> FIRST SALE
  useGetDashboardOpportunitiesQuery,
  useGetPotentialVendorsQuery,
  useGetPotentialRelatedVendorsQuery,
  useGetPotentialOriginManufactuersQuery,
  useGetPotentialOriginExportQuery,
  useGetDashboardUpdateDateQuery,
  useMutateSendFirstSaleEmailMutation,
  useGetAllVendorsQuery,

  // Potentials Dashboard ---> PREFERENTIAL ORIGIN
  useGetPotentialDashboardTariffCodesQuery,
  useGetPotentialDashboardProgramsQuery,
  useGetPotentialDashboardCountriesQuery,

  // Visibility Dashboard ---> ANALYTICS
  useGetDashboardDutySavingUtilizedQuery,
  useGetDashboardManufacturersQuery,
  useGetDashboardImportsQuery,
  useGetDashboardMetricsQuery,
  useGetDashboardTariffHtsQuery,
  useGetDashboardDutySpendQuery,
  useGetDashboardImportsExportsQuery,
  useGetPotentialFTZSavingsQuery,
  useGetPotentialDashboardMTBSavingsQuery,
  useGetPotentialDashboardExclusionSavingsQuery,
} = dashboardApi;
